import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ResponsiveContainer, AreaChart, Area, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { TrendingUp, Trophy } from "lucide-react";
import { api } from "../../lib/api";

const card = "rounded-2xl border border-white/10 bg-surface/60 p-5";

function Kpi({ label, value, sub, testId }) {
  return (
    <div className={card} data-testid={testId || `kpi-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <p className="text-[11px] font-semibold uppercase tracking-widest text-white/40">{label}</p>
      <p className="mt-2 font-display text-3xl font-bold text-white">{value ?? 0}</p>
      {sub && <p className="mt-1 text-xs text-gold">{sub}</p>}
    </div>
  );
}

function Table({ title, cols, rows, keyFn, testId }) {
  return (
    <div className={`${card} overflow-x-auto`} data-testid={testId}>
      <h3 className="mb-3 font-display text-lg font-semibold text-white">{title}</h3>
      <table className="w-full text-left text-sm">
        <thead><tr className="text-[11px] uppercase tracking-widest text-white/40">{cols.map((c) => <th key={c.k} className="pb-2 pr-3">{c.l}</th>)}</tr></thead>
        <tbody>
          {rows.map((r, i) => (
            <tr key={keyFn ? keyFn(r) : i} className="border-t border-white/5 text-white/80">
              {cols.map((c) => <td key={c.k} className="py-2 pr-3">{c.render ? c.render(r) : r[c.k]}</td>)}
            </tr>
          ))}
        </tbody>
      </table>
      {rows.length === 0 && <p className="text-sm text-white/40">No data yet for this period.</p>}
    </div>
  );
}

export function PromoterDashboard() {
  const [days, setDays] = useState(30);
  const [d, setD] = useState(null);
  useEffect(() => { api.get(`/admin/promoter/summary?days=${days}`).then((r) => setD(r.data)).catch(() => {}); }, [days]);
  if (!d) return <div className="h-10 w-10 animate-spin rounded-full border-2 border-gold border-t-transparent" />;
  const k = d.kpis;
  const o = d.outbound;
  return (
    <div data-testid="promoter-dashboard">
      <div className="mb-6 flex flex-wrap items-center justify-between gap-3">
        <div>
          <h2 className="font-display text-2xl font-bold uppercase tracking-tight text-white">Promoter Dashboard</h2>
          <p className="mt-1 text-xs text-white/40">First-party data only. Quality traffic and fan conversion — not vanity metrics.</p>
        </div>
        <div className="flex gap-1 rounded-full border border-white/10 p-1">
          {[7, 30, 90].map((n) => <button key={n} data-testid={`range-${n}`} onClick={() => setDays(n)} className={`rounded-full px-3 py-1.5 text-xs font-bold ${days === n ? "bg-gold text-black" : "text-white/60"}`}>{n}d</button>)}
        </div>
      </div>

      {(d.bestCampaign || d.bestSource) && (
        <div className="mb-6 grid gap-3 sm:grid-cols-2">
          {d.bestCampaign && <div className="rounded-2xl border border-gold/40 bg-gold/10 p-4" data-testid="best-campaign"><p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gold"><Trophy size={12} /> Best-converting campaign</p><p className="mt-1 font-display text-xl font-semibold text-white">{d.bestCampaign.title} <span className="text-sm text-white/50">/{d.bestCampaign.slug}</span></p><p className="text-xs text-white/60">{d.bestCampaign.visitors} visitors → {d.bestCampaign.outbound} streaming clicks, {d.bestCampaign.signups} signups ({d.bestCampaign.conversion}%)</p></div>}
          {d.bestSource && <div className="rounded-2xl border border-gold/40 bg-gold/10 p-4" data-testid="best-source"><p className="inline-flex items-center gap-1.5 text-[11px] font-bold uppercase tracking-widest text-gold"><TrendingUp size={12} /> Best-performing source</p><p className="mt-1 font-display text-xl font-semibold capitalize text-white">{d.bestSource.source}</p><p className="text-xs text-white/60">{d.bestSource.visitors} visitors → {d.bestSource.outbound} streaming clicks, {d.bestSource.signups} signups ({d.bestSource.conversion}%)</p></div>}
        </div>
      )}

      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi label="Visitors" value={k.visitors} sub={`${k.newVisitors} new • ${k.returningVisitors} returning (${k.returningRate}%)`} />
        <Kpi label="Streaming clicks" value={k.streamingClicks} sub={`${k.streamingRate}% of visitors`} />
        <Kpi label="Código List signups" value={k.signups} sub={`${k.signupRate}% conversion`} />
        <Kpi label="Shares" value={k.shares} sub={`${k.relatedClicks} related-song clicks`} />
        <Kpi label="Spotify clicks" value={o.spotify} />
        <Kpi label="Apple Music clicks" value={o.appleMusic} />
        <Kpi label="YouTube clicks" value={o.youtube} sub={`${k.videoPlays} in-page video plays`} />
        <Kpi label="Social clicks" value={o.tiktok + o.instagram + o.facebook} sub={`TikTok ${o.tiktok} • IG ${o.instagram} • FB ${o.facebook}`} />
      </div>

      <div className={`${card} mt-6`} data-testid="daily-chart">
        <h3 className="mb-3 font-display text-lg font-semibold text-white">Visitors & streaming clicks per day</h3>
        <div className="h-56">
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={d.daily}>
              <defs><linearGradient id="g1" x1="0" y1="0" x2="0" y2="1"><stop offset="0%" stopColor="#e2b04a" stopOpacity={0.5} /><stop offset="100%" stopColor="#e2b04a" stopOpacity={0} /></linearGradient></defs>
              <CartesianGrid stroke="rgba(255,255,255,0.06)" vertical={false} />
              <XAxis dataKey="date" tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} tickFormatter={(v) => v.slice(5)} />
              <YAxis tick={{ fill: "rgba(255,255,255,0.4)", fontSize: 10 }} width={28} allowDecimals={false} />
              <Tooltip contentStyle={{ background: "#0b0b0f", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 12, fontSize: 12 }} />
              <Area type="monotone" dataKey="visitors" stroke="#e2b04a" fill="url(#g1)" strokeWidth={2} />
              <Area type="monotone" dataKey="outbound" stroke="#1ED760" fill="transparent" strokeWidth={2} />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="mt-6 grid gap-6 lg:grid-cols-2">
        <Table testId="sources-table" title="Traffic sources" rows={d.sources} keyFn={(r) => r.source} cols={[{ k: "source", l: "Source", render: (r) => <span className="capitalize text-white">{r.source}</span> }, { k: "visitors", l: "Visitors" }, { k: "outbound", l: "Streams" }, { k: "signups", l: "Signups" }, { k: "conversion", l: "Conv%", render: (r) => `${r.conversion}%` }]} />
        <Table testId="landing-table" title="Top landing pages" rows={d.landingPages} keyFn={(r) => r.path} cols={[{ k: "path", l: "Page", render: (r) => <code className="text-xs text-white">{r.path}</code> }, { k: "visitors", l: "Visitors" }]} />
        <Table testId="songs-table" title="Top songs" rows={d.topSongs} keyFn={(r) => r.releaseId} cols={[{ k: "title", l: "Song", render: (r) => <span className="inline-flex items-center gap-2 text-white">{r.artwork && <img src={r.artwork} alt="" className="h-7 w-7 rounded" />}{r.slug ? <Link to={`/music/${r.slug}`} className="hover:text-gold">{r.title}</Link> : r.title}</span> }, { k: "views", l: "Views" }, { k: "plays", l: "Plays" }, { k: "outbound", l: "Streams" }]} />
        <Table testId="campaigns-table" title="Campaign pages" rows={d.campaigns} keyFn={(r) => r.slug} cols={[{ k: "title", l: "Campaign", render: (r) => <span className="text-white">{r.title} <span className="text-white/40">/{r.slug}</span></span> }, { k: "visitors", l: "Visitors" }, { k: "ctaClicks", l: "CTA" }, { k: "outbound", l: "Streams" }, { k: "signups", l: "Signups" }, { k: "conversion", l: "Conv%", render: (r) => `${r.conversion}%` }]} />
      </div>

      <div className="mt-6 grid gap-3 sm:grid-cols-3 lg:grid-cols-6">
        <Kpi label="Play starts" value={k.playStarts} sub={`${k.thirtySeconds} reached 30s`} />
        <Kpi label="Campaign CTA" value={k.campaignCtaClicks} />
        <Kpi label="Fan votes" value={k.fanVotes} />
        <Kpi label="EPK views" value={k.pressViews} />
        <Kpi label="Booking inquiries" value={k.bookingSubmits} />
        <Kpi label="Mobile share" value={`${Math.round(((d.devices.mobile || 0) / Math.max(1, k.visitors)) * 100)}%`} />
      </div>
    </div>
  );
}
